import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ScribeHistoryItem, ScribeOutput } from './Interface';
import {
  MicOff,
  FormatListNumbered,
  FormatListBulleted,
  FormatAlignRight,
  FormatAlignCenter,
  FormatAlignLeft,
  FormatUnderlined,
  FormatItalic,
  FormatBold,
  MoreHoriz,
  Redo,
  Send,
  Undo,
  Close,
  Info,
} from '@mui/icons-material';
import Editor, { Toolbar, createButton, createDropdown, Separator } from 'react-simple-wysiwyg';
import ScribeDetailsPanel from './ScribeDetailsPanel';

interface TabPanelProps {
  text: string;
  readOnly: boolean;
}

const BtnUndo = createButton('Undo', <Undo />, 'undo');
const BtnRedo = createButton('Redo', <Redo />, 'redo');
const FontSizeDropdown = createDropdown('14px', [
  ['10px', 'fontSize', '1'],
  ['12px', 'fontSize', '2'],
  ['14px', 'fontSize', '3'],
  ['16px', 'fontSize', '4'],
  ['18px', 'fontSize', '5'],
  ['20px', 'fontSize', '6'],
  ['24px', 'fontSize', '7'],
]);
const BtnBold = createButton('Bold', <FormatBold />, 'bold');
const BtnItalic = createButton('Italic', <FormatItalic />, 'italic');
const BtnUnderline = createButton('Underline', <FormatUnderlined />, 'underline');
const BtnAlignLeft = createButton('Align Left', <FormatAlignLeft />, 'alignLeft');
const BtnAlignCenter = createButton('Align Center', <FormatAlignCenter />, 'alignCenter');
const BtnAlignRight = createButton('Align Right', <FormatAlignRight />, 'alignRight');

const BtnBulletList = createButton('Bullet List', <FormatListBulleted />, 'insertUnorderedList');
const BtnNumberedList = createButton('Numbered List', <FormatListNumbered />, 'insertOrderedList');
const BtnMicrophone = createButton('Microphone', <MicOff />, 'insertMicrophone');

const CustomTabPanel = (props: TabPanelProps) => {
  const { text, readOnly } = props;
  const [content, setContent] = useState<string>(text);
  const editorRef = useRef<any>(null);
  const handleTextChange = (e: any) => {
    // Handle text changes if needed
    // You can add logic here to save changes or update state
    setContent(e.target.value);
  };
  useEffect(() => {
    setContent(text);
  }, [text]);
  return (
    <Box
      height="100%"
      sx={{
        '.rsw-ce': {
          padding: '24px',
          ul: { listStyleType: 'disc', paddingLeft: '20px', color: 'text.primary' },
          color: 'text.primary',
        },
        '.rsw-toolbar': {
          flexShrink: '0 !important',
          gap: '8px !important',
          padding: '8px !important',
          backgroundColor: ' #ffffff !important',
          display: readOnly ? 'none !important' : 'flex !important',
        },
      }}>
      <Editor
        ref={editorRef}
        value={content}
        onChange={handleTextChange}
        containerProps={{
          style: {
            borderLeft: '0px',
            borderBottom: '0px',
            borderRadius: '0px',
            fontSize: '14px',
            lineHeight: '1.6em',
            height: 'calc(100% - 154px)',
          },
        }}
        placeholder="Start typing your letter...">
        <Toolbar style={{ height: '50px' }}>
          <BtnUndo />
          <BtnRedo />
          <Separator />
          <FontSizeDropdown />
          <Separator />
          <BtnBold />
          <BtnItalic />
          <BtnUnderline />
          <Separator />
          <BtnAlignLeft />
          <BtnAlignCenter />
          <BtnAlignRight />
          <Separator />
          <BtnBulletList />
          <BtnNumberedList />
          <Separator />
          <BtnMicrophone />
        </Toolbar>
      </Editor>
    </Box>
  );
};

const ScribeLettersPanel = ({
  scribe,
  handleDeleteLetter,
  scribeSent,
  handleSend,
}: {
  scribe: ScribeHistoryItem;
  handleDeleteLetter: (index: number) => void;
  scribeSent: boolean;
  handleSend: () => void;
}): JSX.Element => {
  const [value, setValue] = useState(0);
  const letters = scribe?.request?.outputs || [];
  return (
    // Horizontal scrollable tabs
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ padding: '16px 24px', borderBottom: '1px solid #e0e0e0', height: '64px', gap: '8px' }}>
        <Typography variant="h6">Scribe</Typography>
        <Stack direction="row" alignItems="center" gap={1} sx={{ marginLeft: 'auto', paddingRight: '16px' }}>
          <Info color="info" />
          <Typography variant="body2">AI can make mistakes. Check important info.</Typography>
        </Stack>
        {!scribeSent && (
          <>
            <FormControl>
              <InputLabel id="template-select-label">Template</InputLabel>
              <Select
                labelId="template-select-label"
                id="template-select"
                sx={{ marginLeft: 'auto', height: '40px' }}
                label="Template"
                variant="outlined"
                value="1">
                <MenuItem value="1" selected>
                  Initial Consultation
                </MenuItem>
                <MenuItem value="2">Follow-up Consultation</MenuItem>
              </Select>
            </FormControl>
            <IconButton>
              <MoreHoriz />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Send />}
              sx={{ borderRadius: '4px' }}
              onClick={handleSend}>
              SEND
            </Button>
          </>
        )}
      </Stack>
      {scribeSent ? (
        <Alert severity="success" sx={{ borderBottom: '1px solid #e0e0e0' }}>
          Scribe outputs have been finalized and sent. Copies are saved to the patient record.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ borderBottom: '1px solid #e0e0e0', color: '#114674', backgroundColor: '#F0F7FD' }}>
          Scribe transcription stopped. Draft notes and letters generated. Please review before sending.
        </Alert>
      )}
      <Tabs
        value={value}
        onChange={(event, newValue) => setValue(newValue)}
        sx={{ padding: '0px 16px', backgroundColor: '#fafafa' }}>
        {<Tab label="Transcript" key={0} sx={{ height: '72px', marginTop: '-8px' }} />}
        {letters.map((letter, index) => (
          <Tab
            key={index + 1}
            label={letter.title}
            icon={
              scribeSent ? (
                <></>
              ) : (
                <Close onClick={() => handleDeleteLetter(index)} sx={{ width: '20px', height: '20px' }} />
              )
            }
            iconPosition="end"
            sx={{ marginTop: '-8px' }}
          />
        ))}
      </Tabs>
      {value === 0 ? (
        <Box sx={{ height: '100%' }}>
          <ScribeDetailsPanel scribe={scribe} scribeSent={scribeSent} />
        </Box>
      ) : (
        <Box sx={{ height: '100%' }}>
          <CustomTabPanel text={letters[value - 1]?.text || ''} readOnly={scribeSent} />
        </Box>
      )}
    </Box>
  );
};

export default ScribeLettersPanel;
